import { NoMarginParagraph } from '@/components/shared/components/common.StyledComponents';
import { FOCUS_RING_SHADOW } from '@/components/shared/constants/focusRing';
import { DDDCard, DDDCardHeader } from '../../Applicant.StyledComponents';
import styled from '@emotion/styled';
import { Button, Label, rem } from '@triumph/design-system';

export const Container = styled.div`
    max-width: ${rem(739)};
    padding: ${rem(32)};
    display: flex;
    flex-direction: column;
    gap: ${rem(34)};
    .page-visit-wrap {
        word-break: break-all;
    }
`;

// The DDD card clips its overflow, which would cut off the filter menu and the note focus ring;
// rounding the header itself keeps the corners instead.
export const ActivityCard = styled(DDDCard)`
    overflow: visible;
`;

export const ActivityCardHeader = styled(DDDCardHeader)`
    align-items: center;
    justify-content: space-between;
    border-radius: ${rem(10)} ${rem(10)} 0 0;
`;

export const ActivityCardBody = styled.div`
    padding: ${rem(20)};
`;

export const FilterButtonsWrapper = styled.div`
    display: flex;
    gap: ${rem(16)};
    align-items: center;
`;

export const FilterMenuList = styled.div`
    display: flex;
    flex-direction: column;
    padding: ${rem(8)} 0;
    min-width: ${rem(200)};
`;

export const FilterMenuRow = styled.label`
    display: flex;
    align-items: center;
    gap: ${rem(12)};
    padding: ${rem(10)} ${rem(16)};
    cursor: pointer;
    &:hover {
        background: ${({ theme }) => theme.colors.greyscale.gray2_5};
    }
`;

export const FilterMenuRowLabel = styled.span`
    font-size: ${rem(14)};
    color: ${({ theme }) => theme.colors.greyscale.gray50};
`;

export const EmptySelectionMessage = styled(NoMarginParagraph)`
    font-style: italic;
    color: ${({ theme }) => theme.colors.greyscale.gray50};
`;

export const NewNoteWrapper = styled.div`
    width: ${rem(561)};
    display: flex;
    flex-direction: column;
    gap: ${rem(16)};
    align-items: end;
`;

// The design system Stepper unconditionally prepends its own AddNewNote as the
// first .stepper-item. We supply our own mention-aware note form as step 0
// instead and pass no addNoteConfig, leaving that injected item empty — so we
// hide it here. Our form then renders as the (visually) first item.
export const StepperWrapper = styled.div`
    .stepper-item:first-of-type {
        display: none;
    }

    .stepper-item {
        padding-left: ${rem(12)};
        padding-right: ${rem(12)};
    }

    /* The hidden first item makes the first visible row even, as in the DDD tables. The Stepper
       sets each item's white background inline, so only !important can override it. */
    .stepper-item:nth-of-type(odd) {
        background: ${({ theme }) => theme.colors.greyscale.gray2_5} !important;
    }
`;

export const HighlightAnchor = styled.div<{ isFocused?: boolean }>`
    margin: ${rem(6)} ${rem(-12)} 0;
    padding: ${rem(4)} ${rem(12)};
    border-radius: 6px;
    transition: box-shadow 200ms ease-in-out;
    box-shadow: ${({ isFocused }) => (isFocused ? FOCUS_RING_SHADOW : 'none')};
`;

export const NewNoteButtonsWrapper = styled.div`
    display: flex;
    gap: ${rem(8)};
`;

export const ResetFilterButton = styled(Button)`
    > div {
        padding-left: 0;
        text-decoration: none;
    }
`;

export const ClearButton = styled(Button)`
    > div {
        text-decoration: none;
    }
`;

export const Status = styled(Label)`
    font-weight: ${({ theme }) => theme.typography.fontWeights.regular};
    font-size: ${rem(14)};
    text-transform: none;
    margin: 0;
    white-space: pre-line !important;
`;

export const Details = styled(NoMarginParagraph)`
    font-style: italic;
    white-space: pre-line !important;
`;

export const SubmissionLinkButton = styled.button`
    display: block;
    background: none;
    border: none;
    padding: 0;
    color: #0000ee;
    text-decoration: underline;
    cursor: pointer;
    font: inherit;
    font-size: ${rem(14)};

    &:hover {
        color: #0000cc;
    }
    &:focus-visible {
        outline: 2px solid #0000ee;
        outline-offset: 2px;
        border-radius: 2px;
    }
`;

export const PageVisitDetails = styled.div`
    font-style: italic;
    white-space: pre-line;
`;

export const EmailHeaderLine = styled.div`
    font-size: ${rem(14)};
    color: ${({ theme }) => theme.colors.greyscale.gray50};
    word-break: break-word;
`;

export const ViewEmailLink = styled.button`
    margin-top: ${rem(4)};
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
    font-size: ${rem(14)};
    color: ${({ theme }) => theme.colors.primary.actionDefault};
    text-decoration: underline;
`;

export const InfoRequestField = styled.div`
    display: flex;
    gap: ${rem(6)};
    font-size: ${rem(14)};
`;

export const InfoRequestFieldLabel = styled.span`
    font-weight: ${({ theme }) => theme.typography.fontWeights.semiBold};
    white-space: nowrap;
`;

export const InfoRequestFieldValue = styled.span`
    color: ${({ theme }) => theme.colors.greyscale.gray50};
    white-space: pre-line;
`;

// Flags a page visit whose device differs from the chronologically-previous visit,
// surfacing the desktop→mobile journey-switch reviewers look for.
export const DeviceChangeBadge = styled.div`
    display: inline-block;
    margin-top: ${rem(4)};
    padding: ${rem(2)} ${rem(8)};
    border-radius: ${rem(4)};
    font-size: ${rem(12)};
    font-style: normal;
    color: ${({ theme }) => theme.colors.semantic.error50};
    background: ${({ theme }) => theme.colors.semantic.error10};
`;
